// @ts-strict-ignore
import { EnvironmentInjector, inject, resolveForwardRef, runInInjectionContext } from "@angular/core";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/ngrx/root.reducer";
import { AppletService } from "src/app/shared/services/applet.service";
import * as C from "../constants/globals.constants";
import { ErrorCheck } from "../models/error-check.model";
import { ProfileDto } from "../models/profile.model";
import { ProgressCheck } from "../models/progress-check.model";
import * as ValidatorModel from "../models/validator.model";
import { ValidatorConditionsDto } from "../models/validator.model";
import { LogService } from "../services/log.service";
import { DataService } from "./data.service";

@Injectable({ providedIn: "root" })
export class ValidatorService {
  constructor(
    private store: Store<AppState>,
    private appletService: AppletService
  ) {}

  private readonly injector = inject(EnvironmentInjector);

  logService = new LogService();
  dataService = new DataService();
  debugMode = false;

  getValidatorNode(validator: ValidatorModel.ValidatorDto, key: string): ValidatorModel.ValidatorNodeDto | null {
    for (const node of validator.nodes) {
      if (node.key === key) {
        return { ...node } as ValidatorModel.ValidatorNodeDto;
      }
    }

    return null;
  }

  getParentNodes(validator: ValidatorModel.ValidatorDto, key: string) {
    const parentNodes: ValidatorModel.ValidatorNodeDto[] = [];
    const currentNode = this.getValidatorNode(validator, key);

    if (currentNode?.parents) {
      for (const node of currentNode.parents) {
        const parentNode = this.getValidatorNode(validator, node);

        if (parentNode !== null) {
          parentNodes.push({ ...parentNode });
        }
      }
    }

    return parentNodes as ValidatorModel.ValidatorNodeDto[];
  }

  isNodeRoot(validator: ValidatorModel.ValidatorDto, key: string) {
    const currentNode = this.getValidatorNode(validator, key);

    if (currentNode?.parents && currentNode.parents.length > 0) {
      return false;
    } else {
      return true;
    }
  }

  getAllChildNodes(validator: ValidatorModel.ValidatorDto, key: string) {
    const childNodes = [];

    for (const node of validator.nodes) {
      if (node.parents) {
        for (const parent of node.parents) {
          if (parent === key) {
            childNodes.push({ ...node });
            break;
          }
        }
      }
    }

    return childNodes as ValidatorModel.ValidatorNodeDto[];
  }

  getTargetValue(values, targetReferences: string[]) {
    let objectReference = values;

    try {
      for (const targetReference of targetReferences) {
        if (typeof objectReference[targetReference] !== "undefined") {
          objectReference = objectReference[targetReference];
        } else {
          return C.TARGET_VALUE_NOT_FOUND;
        }
      }

      return objectReference;
    } catch (e) {
      return C.TARGET_VALUE_NOT_FOUND;
    }
  }

  checkCondition(target, condition, value) {
    if (target === C.TARGET_VALUE_NOT_FOUND) {
      return false;
    }

    switch (condition) {
      case C.CONDITION_EQUAL: {
        return target == value; // Specifically avoiding type checking (ie: ===)
      }
      case C.CONDITION_NOT_EQUAL: {
        return target != value; // Specifically avoiding type checking (ie: !==)
      }
      case C.CONDITION_GREATER_THAN: {
        return target > value;
      }
      case C.CONDITION_GREATER_THAN_OR_EQUAL: {
        return target >= value;
      }
      case C.CONDITION_LESS_THAN: {
        return target < value;
      }
      case C.CONDITION_LESS_THAN_OR_EQUAL: {
        return target <= value;
      }
    }

    return false;
  }

  /**
   * Builds a node's component purely to reach its `errorCheck`/`progressCheck` implementation.
   *
   * Construction runs inside an injection context so that component field initializers calling
   * `inject()` resolve rather than throwing NG0203. Constructor parameters are still not supplied,
   * so these checks must rely only on the arguments passed to them.
   */
  private createNodeComponent(node: ValidatorModel.ValidatorNodeDto): Partial<ErrorCheck & ProgressCheck> | null {
    if (!node?.component) {
      return null;
    }

    const nodeComponent = resolveForwardRef(node.component);

    return runInInjectionContext(this.injector, () => new nodeComponent());
  }

  canProgress(validator: ValidatorModel.ValidatorDto, key: string, values: ProfileDto) {
    const node = this.getValidatorNode(validator, key);
    let canProgress = true;

    try {
      // First check canProgress conditions defined in the validator itself
      if (node?.canProgress) {
        canProgress = this.checkConditions(values, node.canProgress);
      }

      // Next check if node has advanced progressCheck handling and execute it if necessary
      if (canProgress && node.progressCheck) {
        const component = this.createNodeComponent(node);

        if (component?.progressCheck) {
          canProgress = component.progressCheck(values, this.dataService, this.appletService, this.store);
        }
      }

      return canProgress;
    } catch (e) {
      this.logService.log(`[ValidatorService] canProgress() error thrown on key ${key}...`, e);

      return false;
    }
  }

  canActivate(validator: ValidatorModel.ValidatorDto, key: string, values: ProfileDto) {
    let canActivate = false;
    const node = this.getValidatorNode(validator, key);

    try {
      if (node?.canActivate) {
        canActivate = this.checkConditions(values, node.canActivate);
      } else {
        canActivate = true;
      }

      // If the node has parents, checks that at least one of them can be activated
      if (canActivate && node.parents) {
        let canActivateParent = false;

        for (const parentNodeKey of node.parents) {
          const parentNode = this.getValidatorNode(validator, parentNodeKey);

          if (parentNode?.canActivate) {
            canActivateParent = this.checkConditions(values, parentNode.canActivate);
          } else {
            canActivateParent = true;
          }

          // If we can activate one parent, and all of its parents, then return true
          if (canActivateParent) {
            const canActivateParentOfParent = this.canActivate(validator, parentNode.key, values);

            if (canActivateParentOfParent) {
              return true;
            }
          }
        }

        canActivate = canActivateParent;
      }
    } catch (e) {
      this.logService.log(`[ValidatorService] canActivate() error thrown on key ${key}...`, e);
    }

    return canActivate;
  }

  hasErrors(validator: ValidatorModel.ValidatorDto, key: string, values: ProfileDto) {
    const node = this.getValidatorNode(validator, key);

    try {
      if (node?.errorCheck) {
        const component = this.createNodeComponent(node);

        if (component?.errorCheck) {
          return component.errorCheck(values, this.dataService, this.appletService, this.store);
        }
      }

      return false;
    } catch (e) {
      this.logService.log(`[ValidatorService] hasErrors() error thrown on key ${key}...`, e);

      return true;
    }
  }

  checkConditions(values: ProfileDto, conditions: ValidatorConditionsDto[][]) {
    let passedOneConditionArray = false;

    for (const conditionArray of conditions) {
      let conditionArrayPassed = true;

      for (const condition of conditionArray) {
        // The first condition we meet that fails means the particulat conditionArray fails
        if (!this.checkCondition(this.getTargetValue(values, condition.target), condition.condition, condition.value)) {
          conditionArrayPassed = false;
        }
      }

      if (conditionArrayPassed) {
        passedOneConditionArray = true;
      }
    }

    return passedOneConditionArray;
  }

  validateNodeTree(validator: ValidatorModel.ValidatorDto, key: string, values: ProfileDto, constrainToRootChild = "", forceDebug = false) {
    this.debugMode = forceDebug;

    const node = this.getValidatorNode(validator, key);

    if (node) {
      // Check whether the node can be progressed, if not it is incomplete, if so we need to recursively check children
      if (this.hasErrors(validator, key, values)) {
        return "error";
      } else if (this.canProgress(validator, key, values)) {
        let childNodes = this.getAllChildNodes(validator, key);

        // If we're constraining childs to only be nodes off a specific root child, filter out ones that are cross linked
        if (constrainToRootChild !== "") {
          const newChildNodes = [];

          for (const childNode of childNodes) {
            const rootChild = this.getRootChildNodeForNode(validator, childNode.key);

            if (rootChild.key === constrainToRootChild) {
              newChildNodes.push(childNode);
            }
          }

          childNodes = [...newChildNodes];
        }

        // Remove any child nodes of this node that we cannot activate after being able to progress the node
        if (childNodes.length > 0) {
          const newChildNodes = [];

          for (const childNode of childNodes) {
            if (this.canActivate(validator, childNode.key, values)) {
              newChildNodes.push(childNode);
            }
          }

          childNodes = [...newChildNodes];
        }

        if (childNodes.length > 0) {
          let recursiveRetVal = null;
          for (const childNode of childNodes) {
            if (this.canActivate(validator, childNode.key, values)) {
              const retVal = this.validateNodeTree(validator, childNode.key, values, constrainToRootChild, forceDebug);

              // Set recursiveRetVal by priority: errors > incomplete > complete, will return the greatest priority retVal found
              if (retVal === "error") {
                recursiveRetVal = "error";
              } else if (retVal === "incomplete" && recursiveRetVal !== "error") {
                recursiveRetVal = "incomplete";
              } else if (retVal === "complete" && recursiveRetVal === null) {
                recursiveRetVal = "complete";
              }
            }
          }

          if (recursiveRetVal) {
            return recursiveRetVal;
          }
        } else {
          // If there are no child nodes of this node then it will be deemed complete

          return "complete";
        }
      } else {
        return "incomplete";
      }
    } else {
      return "incomplete";
    }

    return "error";
  }

  getRootChildNodeForNode(validator: ValidatorModel.ValidatorDto, key: string) {
    // Using the key, traverses up the parent nodes until we find a node that has root as its parent
    const node = this.getValidatorNode(validator, key);

    if (node) {
      let rootIsParent = false;

      for (const parent of node.parents) {
        if (parent === C.EWIZ_ROOT) {
          rootIsParent = true;
          break;
        }
      }

      if (rootIsParent) {
        return node;
      } else {
        for (const parent of node.parents) {
          return this.getRootChildNodeForNode(validator, parent);
        }
      }
    }

    return null;
  }

  getNextNavNode(validator: ValidatorModel.ValidatorDto, key: string, values: ProfileDto) {
    const node = this.getValidatorNode(validator, key);

    if (node) {
      const childNodes = this.getAllChildNodes(validator, key);

      if (childNodes.length > 0) {
        for (const childNode of childNodes) {
          if (this.canActivate(validator, childNode.key, values)) {
            let rootIsParent = false;

            for (const parent of childNode.parents) {
              if (parent === C.EWIZ_ROOT) {
                rootIsParent = true;
                break;
              }
            }

            if (!rootIsParent) {
              // If the child node found doesn't have root as its parent, this is the next nav node
              return {
                nextNode: childNode,
                rootChild: this.getRootChildNodeForNode(validator, childNode.key)
              };
            } else {
              // If the child node found has root as its parent, find the next child off of itself to nav to
              return this.getNextNavNode(validator, childNode.key, values);
            }
          }
        }
      }
    }

    return null;
  }

  getBackNavNode(validator: ValidatorModel.ValidatorDto, key: string, values: ProfileDto) {
    const node = this.getValidatorNode(validator, key);
    const rootChild = this.getRootChildNodeForNode(validator, node.key);
    const alternateChains = [];

    if (node) {
      const parentNodes = this.getParentNodes(validator, key);
      const checkParentNodeKeys = [];

      for (const parentNode of parentNodes) {
        if (parentNode.key !== C.EWIZ_ROOT) {
          const parentRootChild = this.getRootChildNodeForNode(validator, parentNode.key);

          if (rootChild.key === parentRootChild.key) {
            if (parentNode.component && this.canActivate(validator, parentNode.key, values)) {
              return {
                backNode: parentNode,
                rootChild: parentRootChild
              };
            } else {
              checkParentNodeKeys.push(parentNode.key);
            }
          } else {
            // We need to check the other chain(s)
            let checkingAlternateChain = false;

            for (const alternateChain of alternateChains) {
              if (alternateChain === parentRootChild.key) {
                checkingAlternateChain = true;
                break;
              }
            }

            if (!checkingAlternateChain) {
              alternateChains.push(parentRootChild.key);
            }
          }
        }
      }

      if (checkParentNodeKeys.length > 0) {
        for (const parentNodeKey of checkParentNodeKeys) {
          const backNavNode = this.getBackNavNode(validator, parentNodeKey, values);
          if (backNavNode !== null) {
            return backNavNode;
          }
        }
      }
    }

    if (alternateChains.length > 0) {
      for (let i = alternateChains.length - 1; i >= 0; i--) {
        const returnValue = this.getDeepestProgressionNodeForRootChild(validator, alternateChains[i], values, alternateChains[i]);

        if (returnValue !== null) {
          return {
            backNode: this.getValidatorNode(validator, returnValue.key),
            rootChild: this.getRootChildNodeForNode(validator, returnValue.key)
          };
        }
      }
    }

    return null;
  }

  getDeepestProgressionNodeForRootChild(validator, key: string, values: ProfileDto, rootChild: string) {
    const node = this.getValidatorNode(validator, key);

    if (node) {
      const rootChildNodeForNode = this.getRootChildNodeForNode(validator, node.key);

      if (rootChildNodeForNode.key === rootChild) {
        if (this.canActivate(validator, node.key, values)) {
          if (this.canProgress(validator, node.key, values)) {
            const childNodes = this.getAllChildNodes(validator, node.key);

            let returnValue = null;

            for (const childNode of childNodes) {
              returnValue = this.getDeepestProgressionNodeForRootChild(validator, childNode.key, values, rootChild);

              if (returnValue !== null) {
                return returnValue;
              }
            }

            // We couldn't progress any children, so just return this node
            if (returnValue === null) {
              return node;
            }
          } else {
            return node;
          }
        }
      }
    }

    return null;
  }
}
